import { initializeApp, getApps, FirebaseApp } from 'firebase/app';
import {
  initializeFirestore,
  getFirestore,
  Firestore,
  connectFirestoreEmulator,
  persistentLocalCache,
  persistentMultipleTabManager,
} from 'firebase/firestore';
import { getAuth, Auth, GoogleAuthProvider } from 'firebase/auth';
import { getFunctions, Functions, connectFunctionsEmulator } from 'firebase/functions';
import { httpsCallableWithTimeout as httpsCallable } from './httpsCallableWithTimeout';
import { getMessaging, Messaging } from 'firebase/messaging';
import {
  initializeAppCheck,
  ReCaptchaV3Provider,
  AppCheck,
} from 'firebase/app-check';
import { getAnalytics, Analytics, isSupported } from 'firebase/analytics';
import type {
  SubmitContactRequest,
  SubmitContactRequestResponse,
  ArchiveStoreClassRequest,
  ArchiveStorePackageRequest,
  CreateStoreStripeConnectOnboardingRequest,
  CreateStoreStripeConnectOnboardingResponse,
  CreateStoreClassRequest,
  CreateStoreEmployeeRequest,
  CreateMolishBillingPortalSessionRequest,
  CreateMolishBillingPortalSessionResponse,
  CreateMolishSubscriptionCheckoutRequest,
  CreateMolishSubscriptionCheckoutResponse,
  CreateStoreFromWizardRequest,
  CreateStorePackageRequest,
  CreateStoreSessionRequest,
  CreateStoreUserRequest,
  GetStorePackageUsageStatsRequest,
  DeleteStoreUserRequest,
  ListStoreEmployeeUpcomingOccurrencesRequest,
  ListStoreSessionOccurrencesInRangeRequest,
  ListStoreSessionBookingsRequest,
  ListStoreUserEntitlementsRequest,
  ListStoreCashCollectionPendingEntitlementsRequest,
  ListStoreCashCollectionCompletedEntitlementsRequest,
  CreateStoreUserEntitlementRequest,
  PatchStoreBrandingRequest,
  PatchStoreAcceptPaymentsAddonRequest,
  PatchStoreCashPaymentsAddonRequest,
  StartStorePaymentsSetupFromFeaturesRequest,
  StartStorePaymentsSetupFromFeaturesResponse,
  PatchStoreGeneralSettingsRequest,
  UploadStoreBrandingLogoRequest,
  UploadStoreEmployeeProfilePictureRequest,
  PatchStoreSmsAddonRequest,
  PatchStoreSmsAddonSettingsRequest,
  GetStoreSmsUsageRequest,
  GetStoreSmsUsageResponse,
  PatchStoreClassRequest,
  PatchStoreEmployeeRequest,
  PatchStorePackageRequest,
  PatchStoreSessionBookingRequest,
  CreateStoreSessionBookingRequest,
  SearchStoreMembersForEmployeeRequest,
  StoreMemberSearchHit,
  PatchStoreSessionRequest,
  PatchStoreSessionOccurrenceRequest,
  StoreSessionOccurrence,
  PatchStoreUserEntitlementRequest,
  MarkStoreUserEntitlementCashCollectedRequest,
  PatchStoreUserRequest,
  ArchiveStorePromotionRequest,
  CreateStorePromotionRequest,
  CreateStoreCampaignRequest,
  CreateStoreCampaignResponse,
  ListStorePromotionsRequest,
  ListStoreCampaignDeliveriesRequest,
  ListStoreCampaignDeliveriesResponse,
  PreviewStoreCampaignAudienceRequest,
  PreviewStoreCampaignAudienceResponse,
  PatchStoreCampaignRequest,
  PatchStoreCampaignResponse,
  SendStoreCampaignRequest,
  SendStoreCampaignResponse,
  SendStoreCampaignTestRequest,
  SendStoreCampaignTestResponse,
  PatchStorePromotionRequest,
  StoreClass,
  StoreEmployee,
  StorePromotion,
  StoreEmployeeUpcomingOccurrence,
  StoreModel,
  StorePackage,
  StorePackageUsageStats,
  StoreSession,
  StoreSessionBooking,
  StoreSessionTimetableSlot,
  StoreUser,
  StoreCashCollectionPendingEntitlementListItem,
  StoreCashCollectionCompletedEntitlementListItem,
  StoreUserPackageEntitlement,
  ListStorePaymentsRequest,
  ListStorePaymentsResponse,
  ListStorePayoutsRequest,
  ListStorePayoutsResponse,
  ListStoreCustomDomainsRequest,
  ListStoreCustomDomainsResponse,
  RefreshStoreCustomDomainRequest,
  RefreshStoreCustomDomainResponse,
  RemoveStoreCustomDomainRequest,
  StartStoreCustomDomainRequest,
  StartStoreCustomDomainResponse,
  TransferStoreOwnershipRequest,
  UserProfile,
} from '@molish-gym/shared';
import type { ManageFcmTokenRequest } from '@/types/manageFcmToken';

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
  measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID,
};

const USE_EMULATORS = import.meta.env.VITE_USE_EMULATORS === 'true';

let app: FirebaseApp;
let db: Firestore;
let auth: Auth;
let functions: Functions;
let appCheck: AppCheck | null = null;
let messaging: Messaging | null = null;
let analytics: Analytics | null = null;

if (getApps().length === 0) {
  app = initializeApp(firebaseConfig);
} else {
  app = getApps()[0];
}

// Enable IndexedDB-backed offline persistence so warm reloads serve the dashboard's overview
// queries (members, packages, classes, sessions, timetable) from disk while a background
// refresh happens in parallel. Falls back to the in-memory cache if the browser blocks
// IndexedDB (Safari private mode, third-party cookie restrictions, multi-tab race lost, etc.).
try {
  db = initializeFirestore(app, {
    localCache: persistentLocalCache({ tabManager: persistentMultipleTabManager() }),
  });
} catch (e) {
  console.warn('Firestore persistent cache unavailable, falling back to memory:', e);
  db = getFirestore(app);
}
auth = getAuth(app);
functions = getFunctions(app, 'us-central1');

if (USE_EMULATORS) {
  console.log('🔧 Using Firebase Emulators (Auth uses live)');
  connectFirestoreEmulator(db, 'localhost', 8080);
  connectFunctionsEmulator(functions, 'localhost', 5001);
}

const reCaptchaSiteKey = import.meta.env.VITE_RECAPTCHA_SITE_KEY;
if (reCaptchaSiteKey) {
  try {
    appCheck = initializeAppCheck(app, {
      provider: new ReCaptchaV3Provider(reCaptchaSiteKey),
      isTokenAutoRefreshEnabled: true,
    });
    console.log('🛡️ App Check initialized');
  } catch (e) {
    console.warn('App Check initialization failed:', e);
  }
} else if (USE_EMULATORS) {
  console.log('🔧 App Check: Skipped (no key, emulator mode)');
} else {
  console.warn('⚠️ App Check not initialized: VITE_RECAPTCHA_SITE_KEY not set');
}

if (typeof window !== 'undefined' && 'serviceWorker' in navigator) {
  try {
    messaging = getMessaging(app);
  } catch (e) {
    console.warn('Firebase Messaging not supported:', e);
  }
}

if (!USE_EMULATORS && firebaseConfig.measurementId) {
  isSupported().then((supported) => {
    if (supported) {
      analytics = getAnalytics(app);
    }
  });
}

const googleProvider = new GoogleAuthProvider();

export { app, db, auth, functions, appCheck, messaging, analytics, googleProvider, USE_EMULATORS };

export const Collections = {
  USERS: 'users',
} as const;

export const callGetUserDetails = httpsCallable<
  { email?: string; displayName?: string; photoUrl?: string; locale?: 'en' | 'el' },
  UserProfile
>(functions, 'getUserDetails');

export const callUpdateUserLocale = httpsCallable<{ locale: 'en' | 'el' }, { ok: true }>(
  functions,
  'updateUserLocale',
);

export const callGetMyStores = httpsCallable<Record<string, never> | void, StoreModel[]>(
  functions,
  'getMyStores',
);

export const callCreateStoreFromWizard = httpsCallable<
  CreateStoreFromWizardRequest,
  StoreModel
>(functions, 'createStoreFromWizard');

export const callSubmitContactRequest = httpsCallable<
  SubmitContactRequest,
  SubmitContactRequestResponse
>(functions, 'submitContactRequest');

export const callPatchStoreBranding = httpsCallable<PatchStoreBrandingRequest, StoreModel>(
  functions,
  'patchStoreBranding',
);

export const callPatchStoreGeneralSettings = httpsCallable<
  PatchStoreGeneralSettingsRequest,
  StoreModel
>(functions, 'patchStoreGeneralSettings');

export const callStartStoreCustomDomain = httpsCallable<
  StartStoreCustomDomainRequest,
  StartStoreCustomDomainResponse
>(functions, 'startStoreCustomDomain');

export const callListStoreCustomDomains = httpsCallable<
  ListStoreCustomDomainsRequest,
  ListStoreCustomDomainsResponse
>(functions, 'listStoreCustomDomains');

export const callRefreshStoreCustomDomain = httpsCallable<
  RefreshStoreCustomDomainRequest,
  RefreshStoreCustomDomainResponse
>(functions, 'refreshStoreCustomDomain');

export const callRemoveStoreCustomDomain = httpsCallable<RemoveStoreCustomDomainRequest, { ok: true }>(
  functions,
  'removeStoreCustomDomain',
);

export const callUploadStoreBrandingLogo = httpsCallable<
  UploadStoreBrandingLogoRequest,
  StoreModel
>(functions, 'uploadStoreBrandingLogo');

export const callUploadStoreEmployeeProfilePicture = httpsCallable<
  UploadStoreEmployeeProfilePictureRequest,
  StoreEmployee
>(functions, 'uploadStoreEmployeeProfilePicture');

export const callPatchStoreSmsAddon = httpsCallable<PatchStoreSmsAddonRequest, StoreModel>(
  functions,
  'patchStoreSmsAddon',
);

export const callPatchStoreAcceptPaymentsAddon = httpsCallable<
  PatchStoreAcceptPaymentsAddonRequest,
  StoreModel
>(functions, 'patchStoreAcceptPaymentsAddon');

export const callPatchStoreCashPaymentsAddon = httpsCallable<
  PatchStoreCashPaymentsAddonRequest,
  StoreModel
>(functions, 'patchStoreCashPaymentsAddon');

export const callPatchStoreSmsAddonSettings = httpsCallable<
  PatchStoreSmsAddonSettingsRequest,
  StoreModel
>(functions, 'patchStoreSmsAddonSettings');

export const callGetStoreSmsUsage = httpsCallable<
  GetStoreSmsUsageRequest,
  GetStoreSmsUsageResponse
>(functions, 'getStoreSmsUsage');

/** Body uses `ownerId` (next owner’s Firebase Auth uid). */
export const callTransferStoreOwnership = httpsCallable<
  TransferStoreOwnershipRequest,
  StoreModel
>(functions, 'transferStoreOwnership');

export const callCreateStoreUser = httpsCallable<CreateStoreUserRequest, StoreUser>(
  functions,
  'createStoreUser',
);

export const callPatchStoreUser = httpsCallable<PatchStoreUserRequest, StoreUser>(
  functions,
  'patchStoreUser',
);

export const callDeleteStoreUser = httpsCallable<DeleteStoreUserRequest, { ok: true }>(
  functions,
  'deleteStoreUser',
);

export const callCreateStoreEmployee = httpsCallable<CreateStoreEmployeeRequest, StoreEmployee>(
  functions,
  'createStoreEmployee',
);

export const callPatchStoreEmployee = httpsCallable<PatchStoreEmployeeRequest, StoreEmployee>(
  functions,
  'patchStoreEmployee',
);

export const callCreateStorePackage = httpsCallable<CreateStorePackageRequest, StorePackage>(
  functions,
  'createStorePackage',
);

export const callPatchStorePackage = httpsCallable<PatchStorePackageRequest, StorePackage>(
  functions,
  'patchStorePackage',
);

export const callArchiveStorePackage = httpsCallable<ArchiveStorePackageRequest, StorePackage>(
  functions,
  'archiveStorePackage',
);

export const callListStorePromotions = httpsCallable<ListStorePromotionsRequest, StorePromotion[]>(
  functions,
  'listStorePromotions',
);

export const callCreateStorePromotion = httpsCallable<CreateStorePromotionRequest, StorePromotion>(
  functions,
  'createStorePromotion',
);

export const callPatchStorePromotion = httpsCallable<PatchStorePromotionRequest, StorePromotion>(
  functions,
  'patchStorePromotion',
);

export const callArchiveStorePromotion = httpsCallable<ArchiveStorePromotionRequest, StorePromotion>(
  functions,
  'archiveStorePromotion',
);

export const callCreateStoreCampaign = httpsCallable<
  CreateStoreCampaignRequest,
  CreateStoreCampaignResponse
>(functions, 'createStoreCampaign');

export const callPatchStoreCampaign = httpsCallable<
  PatchStoreCampaignRequest,
  PatchStoreCampaignResponse
>(functions, 'patchStoreCampaign');

export const callPreviewStoreCampaignAudience = httpsCallable<
  PreviewStoreCampaignAudienceRequest,
  PreviewStoreCampaignAudienceResponse
>(functions, 'previewStoreCampaignAudience');

export const callSendStoreCampaign = httpsCallable<
  SendStoreCampaignRequest,
  SendStoreCampaignResponse
>(functions, 'sendStoreCampaign');

export const callSendStoreCampaignTest = httpsCallable<
  SendStoreCampaignTestRequest,
  SendStoreCampaignTestResponse
>(functions, 'sendStoreCampaignTest');

export const callListStoreCampaignDeliveries = httpsCallable<
  ListStoreCampaignDeliveriesRequest,
  ListStoreCampaignDeliveriesResponse
>(functions, 'listStoreCampaignDeliveries');

export const callGetStorePackageUsageStats = httpsCallable<
  GetStorePackageUsageStatsRequest,
  StorePackageUsageStats
>(functions, 'getStorePackageUsageStats');

export const callCreateStoreClass = httpsCallable<CreateStoreClassRequest, StoreClass>(
  functions,
  'createStoreClass',
);

export const callPatchStoreClass = httpsCallable<PatchStoreClassRequest, StoreClass>(
  functions,
  'patchStoreClass',
);

export const callArchiveStoreClass = httpsCallable<ArchiveStoreClassRequest, StoreClass>(
  functions,
  'archiveStoreClass',
);

export const callListStoreSessionOccurrencesInRange = httpsCallable<
  ListStoreSessionOccurrencesInRangeRequest,
  StoreSessionTimetableSlot[]
>(functions, 'listStoreSessionOccurrencesInRange');

export const callListStoreEmployeeUpcomingOccurrences = httpsCallable<
  ListStoreEmployeeUpcomingOccurrencesRequest,
  StoreEmployeeUpcomingOccurrence[]
>(functions, 'listStoreEmployeeUpcomingOccurrences');

export const callCreateStoreSession = httpsCallable<CreateStoreSessionRequest, StoreSession>(
  functions,
  'createStoreSession',
);

export const callPatchStoreSession = httpsCallable<PatchStoreSessionRequest, StoreSession>(
  functions,
  'patchStoreSession',
);

export const callPatchStoreSessionOccurrence = httpsCallable<
  PatchStoreSessionOccurrenceRequest,
  StoreSessionOccurrence
>(functions, 'patchStoreSessionOccurrence');

export const callListStoreUserEntitlements = httpsCallable<
  ListStoreUserEntitlementsRequest,
  StoreUserPackageEntitlement[]
>(functions, 'listStoreUserEntitlements');

export const callListStoreCashCollectionPendingEntitlements = httpsCallable<
  ListStoreCashCollectionPendingEntitlementsRequest,
  StoreCashCollectionPendingEntitlementListItem[]
>(functions, 'listStoreCashCollectionPendingEntitlements');

export const callListStoreCashCollectionCompletedEntitlements = httpsCallable<
  ListStoreCashCollectionCompletedEntitlementsRequest,
  StoreCashCollectionCompletedEntitlementListItem[]
>(functions, 'listStoreCashCollectionCompletedEntitlements');

export const callMarkStoreUserEntitlementCashCollected = httpsCallable<
  MarkStoreUserEntitlementCashCollectedRequest,
  { entitlementId: string }
>(functions, 'markStoreUserEntitlementCashCollected');

export const callCreateStoreUserEntitlement = httpsCallable<
  CreateStoreUserEntitlementRequest,
  StoreUserPackageEntitlement
>(functions, 'createStoreUserEntitlement');

export const callPatchStoreUserEntitlement = httpsCallable<
  PatchStoreUserEntitlementRequest,
  StoreUserPackageEntitlement
>(functions, 'patchStoreUserEntitlement');

export const callListStoreSessionBookings = httpsCallable<
  ListStoreSessionBookingsRequest,
  StoreSessionBooking[]
>(functions, 'listStoreSessionBookings');

export const callPatchStoreSessionBooking = httpsCallable<
  PatchStoreSessionBookingRequest,
  StoreSessionBooking
>(functions, 'patchStoreSessionBooking');

export const callCreateStoreSessionBooking = httpsCallable<
  CreateStoreSessionBookingRequest,
  StoreSessionBooking
>(functions, 'createStoreSessionBooking');

export const callSearchStoreMembersForEmployee = httpsCallable<
  SearchStoreMembersForEmployeeRequest,
  StoreMemberSearchHit[]
>(functions, 'searchStoreMembersForEmployee');

export const callManageFcmToken = httpsCallable<
  ManageFcmTokenRequest,
  { success: boolean }
>(functions, 'manageFcmToken');

export const callCreateMolishSubscriptionCheckout = httpsCallable<
  CreateMolishSubscriptionCheckoutRequest,
  CreateMolishSubscriptionCheckoutResponse
>(functions, 'createMolishSubscriptionCheckout');

export const callCreateMolishBillingPortalSession = httpsCallable<
  CreateMolishBillingPortalSessionRequest,
  CreateMolishBillingPortalSessionResponse
>(functions, 'createMolishBillingPortalSession');

export const callCreateStoreStripeConnectOnboarding = httpsCallable<
  CreateStoreStripeConnectOnboardingRequest,
  CreateStoreStripeConnectOnboardingResponse
>(functions, 'createStoreStripeConnectOnboarding');

export const callStartStorePaymentsSetupFromFeatures = httpsCallable<
  StartStorePaymentsSetupFromFeaturesRequest,
  StartStorePaymentsSetupFromFeaturesResponse
>(functions, 'startStorePaymentsSetupFromFeatures');

export const callRefreshStoreStripeConnectStatus = httpsCallable<
  { storeId: string },
  {
    connect: StoreModel['billing'] extends { connect?: infer C } ? C | null : null;
    onboardingUrl?: string;
  }
>(functions, 'refreshStoreStripeConnectStatus');

export const callListStorePayments = httpsCallable<
  ListStorePaymentsRequest,
  ListStorePaymentsResponse
>(functions, 'listStorePayments');

export const callListStorePayouts = httpsCallable<
  ListStorePayoutsRequest,
  ListStorePayoutsResponse
>(functions, 'listStorePayouts');

export {
  requestNotificationPermission,
  getCurrentFcmToken,
  onForegroundMessage,
  syncFcmToken,
  type FcmTokenSyncResult,
} from './firebaseMessaging';
